"use client";

import React, { useRef } from "react";
import type { QuestionConfig, AnswerData, ValidationRules, SliderUnitOption } from "../types";
import { useTranslation } from '@/app/i18n/client';

interface SliderQuestionProps {
    label: string;
    config: QuestionConfig;
    value: AnswerData;
    onChange: (value: AnswerData) => void;
    validationRules?: ValidationRules;
    error?: string;
    onEnterPress?: () => void; // Callback when Enter is pressed in number input
    isLastField?: boolean; // Is this the last slider field in multi-field slider question
    onFocusNextField?: () => void; // Callback to focus next slider field
    sliderFieldId?: string; // Unique ID for this slider field to enable proper focus management
    disabled?: boolean; // When true, lock the field (used by "ไม่ทราบผล" opt-out)
}

export default function SliderQuestion({ label, config, value, onChange, validationRules, error, onEnterPress, isLastField = true, onFocusNextField, sliderFieldId, disabled = false }: SliderQuestionProps) {
    const { lang } = useTranslation();

    // Helper function to get text in correct language
    const getText = (th: string | undefined, en: string | undefined): string => {
      if (lang === 'en') {
        return en || th || '';
      }
      return th || en || '';
    };

    const normalizeUnits = (units?: SliderUnitOption[], fallbackUnit?: string): SliderUnitOption[] => {
        if (Array.isArray(units) && units.length > 0) {
            const hasExplicitDefault = units.some((unit) => unit.isDefault === true);

            return units.map((unit, index) => ({
                value: unit.value,
                label: unit.label || unit.value,
                labelEn: unit.labelEn || unit.label || unit.value,
                factor: typeof unit.factor === 'number' && unit.factor > 0 ? unit.factor : 1,
                isDefault: unit.isDefault === true || (!hasExplicitDefault && index === 0),
            }));
        }

        if (fallbackUnit) {
            return [{ value: fallbackUnit, label: fallbackUnit, labelEn: fallbackUnit, factor: 1, isDefault: true }];
        }

        return [{ value: '', label: '', labelEn: '', factor: 1, isDefault: true }];
    };

    const normalizedUnits = normalizeUnits((config as QuestionConfig & { units?: SliderUnitOption[] }).units, config.unit);
    const defaultUnit = normalizedUnits.find((unit) => unit.isDefault) || normalizedUnits[0];
    const selectedUnit = typeof value?.unit === 'string' && value.unit !== '' ? value.unit : defaultUnit?.value || '';
    const disableBar = config.disableBar === true;
    const disableMinMax = disableBar && config.disableMinMax === true;
    const fallbackMin = config.min ?? validationRules?.min ?? 0;
    const fallbackMax = config.max ?? validationRules?.max ?? 100;
    const sliderValue = disabled
        ? ""
        : (value?.value !== undefined && value?.value !== "") ? value.value : "";
    const min = fallbackMin;
    const max = fallbackMax;
    const step = config.step || 1;
    const inputRef = useRef<HTMLInputElement>(null);
    const effectivePlaceholder = disableBar && disableMinMax ? (lang === 'en' ? config.placeholderEn : config.placeholder) || config.placeholder || '' : '';

    // The slider thumb can only rest on step-aligned positions from min, so the
    // reachable ceiling is the largest one at or below max — not max itself.
    const maxStepValue = Math.floor((max - min) / step) * step + min;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(e.target.value);
        const steppedValue = Math.round((newValue - min) / step) * step + min;
        onChange({ value: Math.min(Math.max(steppedValue, min), maxStepValue), unit: selectedUnit });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        if (newValue === "") {
            onChange({ value: "", unit: selectedUnit });
            return;
        }

        // Allow decimals up to 4 places. Accept partial entries while typing
        // (e.g. "1." or "1.0") by passing the raw string through; numeric
        // coercion happens on blur. Reject anything that isn't a valid number
        // pattern or that exceeds 4 decimal places.
        const decimalPattern = /^-?\d*\.?\d{0,4}$/;
        if (!decimalPattern.test(newValue)) {
            return;
        }

        // Preserve the raw string while the user is mid-entry (trailing dot,
        // leading zero in a decimal). Otherwise store the numeric value.
        const isPartial = newValue.endsWith(".") || newValue === "-" || /\.\d*0$/.test(newValue);
        onChange({
            value: isPartial ? newValue : Number(newValue),
            unit: selectedUnit,
        });
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Allow empty string or invalid input → keep as empty (user must fill in)
        if (newValue === "" || isNaN(Number(newValue))) {
            onChange({ value: "", unit: selectedUnit });
            return;
        }

        // Round to a maximum of 4 decimal places.
        const roundTo4 = (n: number) => Math.round(n * 10000) / 10000;

        if (disableMinMax) {
            onChange({ value: roundTo4(Number(newValue)), unit: selectedUnit });
            return;
        }

        // Clamp value between min and the nearest step-aligned position at or
        // below max (maxStepValue) — not raw max, which may not be reachable.
        const steppedValue = Math.round((Number(newValue) - min) / step) * step + min;
        onChange({ value: roundTo4(Math.min(Math.max(steppedValue, min), maxStepValue)), unit: selectedUnit });
    };

    const handleUnitChange = (unitValue: string) => {
        // ล้างค่าเมื่อเปลี่ยน unit เพื่อให้ user กรอกค่าใหม่ในหน่วยที่เลือก
        onChange({ value: "", unit: unitValue });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && sliderValue !== "") {
            e.preventDefault();

            // Block advancing/focus-next when this field's typed value is out of
            // range. Enter on a non-last field goes through onFocusNextField,
            // which never reaches the page-level validation — so an out-of-range
            // value would otherwise slip past here. disableMinMax fields (free
            // input) are exempt, matching handleInputBlur.
            if (!disableMinMax) {
                const numericValue = Number(sliderValue);
                if (
                    !isNaN(numericValue) &&
                    (numericValue < min || numericValue > max)
                ) {
                    return;
                }
            }

            if (isLastField) {
                // Last field: proceed to next question
                if (onEnterPress) {
                    onEnterPress();
                }
            } else {
                // Not last field: focus next slider field
                if (onFocusNextField) {
                    onFocusNextField();
                }
            }
        }
    };

    // Calculate percentage for progress indicator, clamped between 0-100
    const numericSliderValue = sliderValue === "" || sliderValue === undefined || sliderValue === null || isNaN(Number(sliderValue)) ? min : Number(sliderValue);
    const clampedValue = Math.min(Math.max(numericSliderValue, min), max);
    const percentage = max === min ? 0 : ((clampedValue - min) / (max - min)) * 100;

    const unitSelector = normalizedUnits.length > 0 && (
        <div className={`h-[52px] p-1 bg-[rgba(110,112,112,0.15)] rounded-xl flex items-center gap-1 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {normalizedUnits.map((unit) => {
                const isActive = unit.value === selectedUnit;
                return (
                    <button
                        key={unit.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => handleUnitChange(unit.value)}
                        className={`min-w-[70px] h-[44px] px-3 rounded-lg text-sm transition-all ${isActive ? 'bg-white shadow-[0_2px_4px_rgba(0,0,0,0.18)] text-black' : 'bg-transparent text-black/75'} ${disabled ? 'cursor-not-allowed' : ''}`}
                    >
                        {getText(unit.label, unit.labelEn)}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="flex-1 flex">
            <div className="space-y-2 flex-1 lg:flex-row flex-col">
                <div className={`space-y-2 flex gap-4 flex-col ${disableBar ? '' : 'md:items-center md:flex-row'}`}>
                    <div className={disableBar
                        ? 'flex flex-col gap-3 w-full'
                        : 'flex gap-4 flex-col min-[480px]:flex-row min-[480px]:items-center w-full md:w-auto md:flex-none'
                    }>
                        <div className={disableBar ? 'w-full' : 'min-w-[200px] md:w-[200px] md:min-w-[200px]'}>
                            <h3 className="text-lg font-medium">{label}</h3>
                        </div>

                        {/* Manual input field - show on mobile first row.
                            disableBar (textfield-only) mode: input and unit
                            selector wrap onto their own row and can wrap again
                            between each other on very narrow phones, instead of
                            forcing a single row wider than the viewport — that
                            overflow was pushing the label off-screen via the
                            browser's horizontal auto-scroll-into-view on focus. */}
                        <div className={`flex flex-wrap justify-end w-full gap-2 ${disableBar ? 'md:flex' : 'md:hidden'}`}>
                            <input
                                ref={inputRef}
                                id={`${sliderFieldId}-mobile`}
                                data-slider-field
                                step={step}
                                value={sliderValue}
                                type="text"
                                inputMode="decimal"
                                placeholder={effectivePlaceholder}
                                disabled={disabled}
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                onKeyDown={handleKeyDown}
                                className={`font-semibold focus:outline-none focus:border-[#2A807B] ${disableBar ? 'flex-1 basis-full sm:basis-0 h-[52px] px-5 py-3 border border-[#2A807B] rounded-lg text-left min-w-0 bg-white' : 'w-24 p-3 border border-gray-300 rounded-md text-right'} ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                            />
                            {unitSelector}
                        </div>
                    </div>

                    {/* Slider container */}
                    {!disableBar && <div className={`flex-1 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div className="relative">
                            {/* Background track (right side - unfilled) */}
                            <div
                                className="absolute top-1/3 w-full h-2 rounded-full"
                                style={{ backgroundColor: "#89AEAC59" }}></div>

                            {/* Progress track (left side - filled) */}
                            <div
                                className="absolute top-1/3 h-2 rounded-full"
                                style={{ width: `${percentage}%`, backgroundColor: "#2A807B" }}
                            />

                            {/* Slider input */}
                            <input
                                type="range"
                                min={min}
                                max={max}
                                step={step}
                                value={clampedValue}
                                disabled={disabled}
                                onChange={handleChange}
                                className="relative w-full h-2 bg-transparent appearance-none cursor-pointer
                                [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:w-6
                                [&::-webkit-slider-thumb]:h-6
                                [&::-webkit-slider-thumb]:rounded-full
                                [&::-webkit-slider-thumb]:bg-[#0CA182]
                                [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_white]
                                [&::-webkit-slider-thumb]:cursor-pointer
                                [&::-webkit-slider-thumb]:hover:scale-110
                                [&::-moz-range-thumb]:w-6
                                [&::-moz-range-thumb]:h-6
                                [&::-moz-range-thumb]:rounded-full
                                [&::-moz-range-thumb]:bg-[#0CA182]
                                [&::-moz-range-thumb]:box-shadow-[0_0_0_2px_white]
                                [&::-moz-range-thumb]:cursor-pointer
                                [&::-moz-range-thumb]:hover:scale-110
                                focus:outline-none"
                            />
                        </div>
                        {!disableMinMax && <div className="flex items-center justify-between mt-2 text-[#2A807B]">
                            <span className="text-sm">{min}</span>
                            <span className="text-sm">{max}</span>
                        </div>}
                    </div>}

                    {/* Manual input field - show on desktop */}
                    {!disableBar && <div className="hidden md:flex items-center gap-3">
                        <input
                            ref={inputRef}
                            id={`${sliderFieldId}-desktop`}
                            data-slider-field
                            step={step}
                            value={sliderValue}
                            type="text"
                            inputMode="decimal"
                            placeholder={effectivePlaceholder}
                            disabled={disabled}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                            className={`w-24 p-3 border border-gray-300 rounded-md text-right font-semibold focus:outline-none focus:border-[#2A807B] ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                        />
                        {unitSelector}
                    </div>}
                </div>

                {error && <p className="error-text">{error}</p>}
            </div>
        </div>
   

   
    );
}
